import {
  useEffect,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  BarChart3,
  CalendarCheck2,
  Clock,
  Target,
  type LucideProps,
} from "lucide-react";

import styles from "./auth-layout.module.css";
import { TeamStack } from "./team-stack/team-stack.tsx";
import {
  DEFAULT_TEAM_PHOTOS,
  type TeamPhoto,
} from "./team-stack/types.ts";

type IconComponent = ComponentType<LucideProps>;

export type AuthFeatureTint = "sky" | "amber" | "indigo" | "red";
export type AuthWhatsNewTag = "live" | "new" | "coming";

export interface AuthLayoutFeature {
  icon: IconComponent;
  label: string;
  tint?: AuthFeatureTint;
}

export interface AuthLayoutWhatsNewItem {
  tag: AuthWhatsNewTag;
  text: string;
}

export interface AuthLayoutProps {
  variant?: "default" | "sqe";
  title: string;
  subtitle?: string;
  welcomeMessage?: string | null;
  /** Logo shown in the top-left of the brand panel. */
  brandLogoSrc?: string;
  brandLogoAlt?: string;
  /** Use for company marks that are composed from text or multiple colors. */
  brandLogoContent?: ReactNode;
  copyrightText?: string;
  /** When set, hero shows “Welcome to ” plus this wordmark image instead of `brandHeading`. */
  brandHeroWordmarkSrc?: string | null;
  brandHeading?: ReactNode;
  brandSubheading?: string;
  features?: AuthLayoutFeature[] | null;
  /** Rotating "what's new" strip above the form heading on the right side. */
  whatsNew?: AuthLayoutWhatsNewItem[] | null;
  photos?: TeamPhoto[] | null;
  children: ReactNode;
}

const DEFAULT_BRAND_HEADING = "Welcome to CaeliusOne";
const DEFAULT_BRAND_SUBHEADING =
  "Your unified employee experience \u2014 attendance, timesheets, leaves, projects, and more, all in one place.";

const DEFAULT_FEATURES: AuthLayoutFeature[] = [
  { icon: Clock, label: "Smart Attendance" },
  { icon: CalendarCheck2, label: "Leave Planning" },
  { icon: BarChart3, label: "Team Insights" },
  { icon: Target, label: "Performance Reviews" },
];

const DEFAULT_WHATS_NEW: AuthLayoutWhatsNewItem[] = [
  { tag: "live", text: "Smart timesheet drafting now available" },
  { tag: "new", text: "Team availability dashboard rolled out" },
  { tag: "coming", text: "AI-assisted leave planner in development" },
];

const TAG_LABEL: Record<AuthWhatsNewTag, string> = {
  live: "Just shipped",
  new: "New",
  coming: "Roadmap",
};

const WHATS_NEW_ROTATION_MS = 6000;

const PUBLIC_ASSET_BASE = process.env.PUBLIC_URL ?? "";

const SQE_ASSET_STYLES = {
  "--sqe-login-left-background": `url("${PUBLIC_ASSET_BASE}/assets/sqe-login/sqe-login-left-background-1920x1080.png")`,
  "--sqe-login-overlay": `url("${PUBLIC_ASSET_BASE}/assets/sqe-login/sqe-login-decorative-overlay-1920x1080.png")`,
  "--sqe-login-right-background": `url("${PUBLIC_ASSET_BASE}/assets/sqe-login/sqe-login-right-background-1920x1080.png")`,
} as CSSProperties;

export function AuthLayout({
  variant = "default",
  title,
  subtitle,
  welcomeMessage,
  brandLogoSrc = "/assets/caelius-logo.svg",
  brandLogoAlt = "Caelius Consulting",
  brandLogoContent,
  copyrightText,
  brandHeroWordmarkSrc = null,
  brandHeading = DEFAULT_BRAND_HEADING,
  brandSubheading = DEFAULT_BRAND_SUBHEADING,
  features = DEFAULT_FEATURES,
  whatsNew = DEFAULT_WHATS_NEW,
  photos = DEFAULT_TEAM_PHOTOS,
  children,
}: AuthLayoutProps) {
  const year = new Date().getFullYear();

  const whatsNewCount = whatsNew?.length ?? 0;
  const [whatsNewIdx, setWhatsNewIdx] = useState(0);

  useEffect(() => {
    if (whatsNewCount <= 1) return;
    const id = window.setInterval(() => {
      setWhatsNewIdx((prev) => (prev + 1) % whatsNewCount);
    }, WHATS_NEW_ROTATION_MS);
    return () => window.clearInterval(id);
  }, [whatsNewCount]);

  const activeWhatsNew =
    whatsNew != null && whatsNewCount > 0
      ? whatsNew[whatsNewIdx % whatsNewCount]
      : null;

  return (
    <div
      className={`${styles.root} ${variant === "sqe" ? styles.rootSqe : ""}`}
      style={variant === "sqe" ? SQE_ASSET_STYLES : undefined}
    >
      <aside className={styles.brand}>
        <span className={`${styles.blob} ${styles.blobOne}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobTwo}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobThree}`} aria-hidden="true" />

        <div className={styles.brandTop}>
          {brandLogoContent ?? (
            <img
              className={styles.brandLogo}
              src={brandLogoSrc}
              alt={brandLogoAlt}
            />
          )}
        </div>

        <div className={styles.brandMiddle}>
          {brandHeroWordmarkSrc != null && brandHeroWordmarkSrc !== "" ? (
            <h1 className={styles.brandHeadingLogoWrap}>
              <span className={styles.brandHeadingLead}>Welcome to </span>
              <span className={styles.brandHeroWordmarkSlot}>
                <img
                  className={styles.brandHeroWordmarkImg}
                  src={brandHeroWordmarkSrc}
                  alt="CaeliusOne"
                />
              </span>
            </h1>
          ) : (
            <h1 className={styles.brandHeading}>{brandHeading}</h1>
          )}
          <p className={styles.brandSubheading}>{brandSubheading}</p>

          {features != null && features.length > 0 ? (
            <ul className={styles.features}>
              {features.map(({ icon: Icon, label, tint = "sky" }) => {
                const tintClass =
                  tint === "amber"
                    ? styles.featurePillAmber
                    : tint === "indigo"
                      ? styles.featurePillIndigo
                      : tint === "red"
                        ? styles.featurePillRed
                        : styles.featurePillSky;
                return (
                  <li
                    key={label}
                    className={`${styles.featurePill} ${tintClass}`}
                  >
                    <span className={styles.featureIcon}>
                      <Icon size={18} strokeWidth={2.25} />
                    </span>
                    <span className={styles.featureLabel}>{label}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        <div className={styles.brandBottom}>
          {photos != null && photos.length > 0 ? (
            <TeamStack photos={photos} />
          ) : null}

          <span className={styles.copyright}>
            {copyrightText ?? `© ${year} Caelius Consulting. All rights reserved.`}
          </span>
        </div>
      </aside>

      <div className={styles.mobileLogo}>
        {brandLogoContent ?? (
          <img
            className={
              brandHeroWordmarkSrc != null && brandHeroWordmarkSrc !== ""
                ? styles.mobileLogoWordmark
                : undefined
            }
            src={
              brandHeroWordmarkSrc != null && brandHeroWordmarkSrc !== ""
                ? brandHeroWordmarkSrc
                : brandLogoSrc
            }
            alt={
              brandHeroWordmarkSrc != null && brandHeroWordmarkSrc !== ""
                ? "CaeliusOne"
                : brandLogoAlt
            }
          />
        )}
      </div>

      <main className={styles.formPanel}>
        <div className={styles.formInner}>
          <header className={styles.formHeader}>
            <h2 className={styles.formTitle}>{title}</h2>
            {subtitle != null && subtitle !== "" ? (
              <p className={styles.formSubtitle}>{subtitle}</p>
            ) : null}
          </header>

          {welcomeMessage != null && welcomeMessage !== "" ? (
            <p className={styles.welcomeBanner} role="status">
              {welcomeMessage}
            </p>
          ) : null}

          {children}

          {activeWhatsNew != null ? (
            <div
              className={styles.whatsNewStrip}
              role="status"
              aria-live="polite"
            >
              <span className={styles.whatsNewDot} aria-hidden="true" />
              <span className={styles.whatsNewTag}>
                {TAG_LABEL[activeWhatsNew.tag]}
              </span>
              <span key={whatsNewIdx} className={styles.whatsNewText}>
                {activeWhatsNew.text}
              </span>
              {whatsNewCount > 1 ? (
                <span
                  className={styles.whatsNewProgress}
                  aria-hidden="true"
                  style={{
                    animationDuration: `${WHATS_NEW_ROTATION_MS}ms`,
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
